import React from "react";
import { Box, Button, Typography, Stack, alpha } from "@mui/material";
import {
  Add as AddIcon,
  FlashOn as SetupIcon,
  Description as PageIcon,
  Language as SiteIcon,
} from "@mui/icons-material";

/**
 * Welcome state shown when no sites exist
 */
export function WelcomeState({ onCreateSite }: { onCreateSite: () => void }) {
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "grey.100",
      }}
    >
      <Box sx={{ textAlign: "center", maxWidth: 500, px: 3 }}>
        <Box
          sx={{
            width: 96,
            height: 96,
            borderRadius: 4,
            background: "linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 4,
            boxShadow: "0 8px 32px rgba(59, 130, 246, 0.3)",
          }}
        >
          <SiteIcon sx={{ fontSize: 48, color: "white" }} />
        </Box>

        {/* Rename: CMS -> MeWeb */}
        <Typography variant="h4" color="text.primary" fontWeight={700} gutterBottom>
          Welcome to MeWeb!
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 5, fontSize: "1.1rem" }}>
          สร้าง Site แรกของคุณเพื่อเริ่มต้น
        </Typography>

        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={onCreateSite}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
            boxShadow: "0 4px 14px rgba(16, 185, 129, 0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
            },
          }}
        >
          สร้าง Site ใหม่
        </Button>
      </Box>
    </Box>
  );
}

/**
 * Empty state shown when site is selected but no pages or no page selected
 */
export function EmptyState({
  hasPages,
  onOpenPages,
  onQuickSetup,
  onCreatePage,
}: {
  hasPages: boolean;
  onOpenPages: () => void;
  onQuickSetup: () => void;
  onCreatePage: () => void;
}) {
  if (!hasPages) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.100",
        }}
      >
        <Box sx={{ textAlign: "center", maxWidth: 500, px: 3 }}>
          <Box
            sx={{
              width: 96,
              height: 96,
              borderRadius: 4,
              background: "linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 4,
              boxShadow: "0 8px 32px rgba(59, 130, 246, 0.3)",
            }}
          >
            <SetupIcon sx={{ fontSize: 48, color: "white" }} />
          </Box>

          <Typography variant="h4" color="text.primary" fontWeight={700} gutterBottom>
            Site พร้อมใช้งานแล้ว!
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 5, fontSize: "1.1rem" }}>
            เริ่มสร้างหน้าเว็บของคุณ
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              startIcon={<SetupIcon />}
              onClick={onQuickSetup}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
                boxShadow: "0 4px 14px rgba(245, 158, 11, 0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #D97706 0%, #B45309 100%)",
                },
              }}
            >
              Quick Setup
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<AddIcon />}
              onClick={onCreatePage}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                borderColor: "grey.400",
                color: "text.secondary",
                "&:hover": {
                  borderColor: "grey.500",
                  bgcolor: alpha("#000", 0.04),
                },
              }}
            >
              Create Page
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "grey.100",
      }}
    >
      <Box sx={{ textAlign: "center", maxWidth: 400, px: 3 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: 3,
            bgcolor: "grey.200",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 3,
          }}
        >
          <PageIcon sx={{ fontSize: 40, color: "grey.500" }} />
        </Box>

        <Typography variant="h5" color="text.primary" fontWeight={600} gutterBottom>
          Select a Page
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 4 }}>
          Choose a page from the sidebar to start editing
        </Typography>

        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="contained"
            onClick={onOpenPages}
            sx={{
              px: 3,
              borderRadius: 2,
              boxShadow: "0 4px 14px rgba(59, 130, 246, 0.3)",
            }}
          >
            Open Pages
          </Button>
          <Button
            variant="outlined"
            onClick={onCreatePage}
            sx={{
              px: 3,
              borderRadius: 2,
              borderColor: "grey.400",
              color: "text.secondary",
              "&:hover": {
                borderColor: "grey.500",
                bgcolor: alpha("#000", 0.04),
              },
            }}
          >
            New Page
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
